import { zodResolver } from '@hookform/resolvers/zod'
import { Descriptions, Tag } from 'antd'
import type { HookAPI } from 'antd/es/modal/useModal'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import AtomFormInput from '@/components/Atoms/AtomFormInput/AtomFormInput'
import AtomModalWithForm from '@/components/Atoms/AtomModalWithForm/AtomModalWithForm'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type { FormInputControlType, FormInputRegisterType } from '@/types'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import handleFormSubmitError from '@/utils/forms/handleFormSubmitError'

import { stockImportsApi } from '../api/stockImportsApi'
import { formatMissingSkuReason } from '../lib/stockImportsMessages'
import {
  positionCheckSchema,
  type PositionCheckValues,
} from '../lib/stockImportsValidation'
import cn from '../StockImportsTab/StockImportsTab.module.scss'
import {
  PositionCheckResponse,
  StockImportConfigShortModel,
} from '../types/stockImports.types'
import positionCn from './PositionCheckModal.module.scss'

interface Props {
  open: boolean
  config: StockImportConfigShortModel | null
  modal: HookAPI
  onClose: () => void
}

const positionCheckStatusMap: Record<
  PositionCheckResponse['status'],
  { label: string; color: string }
> = {
  SUCCESS: { label: 'Загружен', color: 'success' },
  REJECTED: { label: 'Отклонен', color: 'warning' },
  NOT_FOUND_IN_FILE: { label: 'Не найден в файле', color: 'default' },
}

const formatLogDate = (value?: string) => {
  if (!value) return '—'

  const date = dayjs(value)
  return date.isValid() ? date.format('DD.MM.YYYY, HH:mm:ss') : '—'
}

function PositionCheckModal({ open, config, modal, onClose }: Props) {
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<PositionCheckResponse[] | null>(null)
  const isOrganisationSelected = useUserInfoStore(
    (state) => Object.keys(state.selectedOrganisation).length > 0
  )
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PositionCheckValues>({
    resolver: zodResolver(positionCheckSchema),
    defaultValues: { article: '' },
    mode: 'onTouched',
    shouldFocusError: false,
  })

  const checkPosition = async (values: PositionCheckValues) => {
    if (!config || loading) return

    setResults(null)
    setLoading(true)
    const toastId = toast.loading('Проверка позиции...')
    try {
      const response = await stockImportsApi.checkPosition(
        config.id,
        values.article
      )
      setResults(response)
      if (response.length === 0) {
        toast.warning('По артикулу ничего не найдено', { id: toastId })
      } else if (response.some((item) => item.status === 'SUCCESS')) {
        toast.success('Позиция найдена', { id: toastId })
      } else {
        toast.warning(
          formatMissingSkuReason(response[0].missingReason) ||
            'Позиция не прошла проверку',
          { id: toastId }
        )
      }
    } catch (error) {
      toast.dismiss(toastId)
      handleApiError(error, modal)
    } finally {
      setLoading(false)
    }
  }

  return (
    <AtomModalWithForm
      title="Проверка позиции"
      submitText="Проверить"
      open={open}
      width={680}
      submitting={loading}
      onCancel={onClose}
      onSubmit={(event) => {
        event.preventDefault()
        void handleSubmit(checkPosition, () => handleFormSubmitError(false))()
      }}
    >
      <AtomFormInput
        layout="stacked"
        idPrefix="position-check-"
        location="positionCheck"
        isOrganisationSelected={isOrganisationSelected}
        name="article"
        title="Артикул (производителя)"
        placeholder="Например: 4626-120"
        required
        errors={errors}
        register={register as FormInputRegisterType}
        control={control as FormInputControlType}
      />
      {results && results.length === 0 && (
        <div className={cn.emptyState}>
          По этому артикулу производителя ничего не найдено
        </div>
      )}
      {results && results.length > 0 && (
        <div className={positionCn.results}>
          {results.map((result, index) => (
            <Descriptions
              key={`${index}-${result.article}-${result.fileName ?? ''}-${result.sheetName ?? ''}`}
              bordered
              size="small"
              column={1}
              title={
                results.length > 1
                  ? `Запись ${index + 1} из ${results.length}`
                  : undefined
              }
            >
              <Descriptions.Item label="Статус">
                <Tag color={positionCheckStatusMap[result.status].color}>
                  {positionCheckStatusMap[result.status].label}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Артикул (производителя)">
                {result.article}
              </Descriptions.Item>
              <Descriptions.Item label="Товар в базе">
                {result.isProductFoundInDb ? 'Да' : 'Нет'}
              </Descriptions.Item>
              <Descriptions.Item label="Остаток">
                {result.quantityInStock ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Причина">
                {formatMissingSkuReason(result.missingReason) || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Дата лога">
                {formatLogDate(result.logDate)}
              </Descriptions.Item>
              <Descriptions.Item label="Файл">
                {result.fileName ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Лист">
                {result.sheetName ?? '—'}
              </Descriptions.Item>
            </Descriptions>
          ))}
        </div>
      )}
    </AtomModalWithForm>
  )
}

export default PositionCheckModal
